import { Plus, CaretDown, MagnifyingGlass } from "@phosphor-icons/react"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { Avatar } from "@tokenterminal/core/ui/icon/Avatar"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { ComboboxWithPinnedItems } from "@tokenterminal/core/ui/samsCombobox/ComboboxWithPinnedItems"
import { ProjectLogo } from "components/project/ProjectLogo"
import { useProjectsList } from "modules/chart-builder/components/SeriesTab/SerieSelector/FilterSelector/useProjectsList"
import { useMemo } from "react"
import { type ChartImageState } from "../types"

export function ProjectLogoDropdown({
  state,
  setState,
}: {
  state: ChartImageState
  setState: (state: ChartImageState) => void
}) {
  const { data: baseProjects = [] } = useProjectsList()

  const projects = useMemo(() => {
    return [
      ...baseProjects,
      {
        id: "plasma",
        name: "Plasma",
        logo: "plasma/logo.png",
      },
    ]
  }, [baseProjects])

  const items = useMemo(() => {
    const projectItems = projects?.map((item) => ({
      id: item.id,
      value: item.logo,
      children: (
        <div className="flex gap-2 items-center justify-start">
          {item.logo ? (
            <ProjectLogo
              name={item.name}
              logo={item.logo}
              size={16}
              borderRadius="rounded"
              loading="eager"
            />
          ) : null}
          {item.name}
        </div>
      ),
      onClick: () =>
        setState({
          ...state,
          image: {
            ...state.image,
            type: "project",
            projectId: item.id,
            projectName: item.name,
            projectLogo: item.logo,
          },
        }),
    }))

    return projectItems
  }, [state, projects, setState])

  const selectedItem = useMemo(() => {
    if (state.image?.projectId) {
      return (
        <div className="flex gap-2 items-center justify-start">
          {state.image.projectLogo && (
            <Avatar
              src={`https://static1.tokenterminal.com/${state.image.projectLogo}`}
              className="w-4 h-4 rounded-full overflow-hidden flex-shrink-0"
            />
          )}
          {state.image.projectName}
        </div>
      )
    }

    return (
      items.find((item) => item.id === state.theme)?.children || (
        <div className="flex gap-2 items-center justify-start">
          Select a project
        </div>
      )
    )
  }, [
    items,
    state.image?.projectId,
    state.image?.projectLogo,
    state.image?.projectName,
    state.theme,
  ])

  return (
    <ComboboxWithPinnedItems
      drawerHeaderTitle="Select a chain"
      trigger={
        state.theme ? (
          <Button
            variant={state.theme ? undefined : "ghost"}
            className="justify-between"
          >
            {selectedItem}
            <Icon as={state.theme ? CaretDown : Plus} />
          </Button>
        ) : (
          <Button className="justify-start">
            <Icon as={MagnifyingGlass} />
            Search projects
          </Button>
        )
      }
      virtualized
      placement="bottom right"
      closeOnSelect
      items={items}
      selectedIds={state.image?.projectId ? [state.image.projectId] : []}
    />
  )
}
